import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { RiCloseLine } from "react-icons/ri";
import Input from "../../../Utility/Input/Input";
import Typography from "../../../Utility/Typography";
import Button from "../../../Utility/Button/Button";
import FormSubmitButton from "../../../Utility/Button/FormSubmitButton";
import ErrorMsg from "../../../Utility/ErrorMsg/ErrorMsg";
import Dropdown from "../../../Utility/Dropdown/Dropdown";
import { logger } from "../../../utils/logger";
import { useDispatch } from "react-redux";
import { updateRole } from "../../../Redux/Thunks/rolesThunk";
import { showSuccess } from "../../../utils/toast";
import { getThunkRejectMessage } from "../../../utils/thunkErrorMessage";
import { isGlobalApiErrorModalOpen } from "../../../utils/globalApiErrorModal";
import { EditRoleSchema } from "../../../FormsValidations/Validations";

const EditRole = ({ isOpen, onClose, onSuccess, role }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const normalizeRoleName = (value = "") => String(value).trim().replace(/\s+/g, "_").toUpperCase();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(EditRoleSchema),
    defaultValues: {
      roleName: "",
      description: "",
      status: "Active",
    },
  });

  // Reset form with role data when modal opens
  useEffect(() => {
    if (isOpen && role) {
      reset({
        roleName: role.name || "",
        description: role.description || "",
        status: role.status || "Active",
      });
    }
  }, [isOpen, role, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (!role?.id) throw new Error("Role ID is required");
      const payload = {
        name: normalizeRoleName(data.roleName),
        description: (data.description || "").trim(),
      };
      const updatedRole = await dispatch(updateRole({ id: role.id, roleData: payload })).unwrap();
      logger.info("Role updated:", updatedRole);
      showSuccess("Role updated successfully");

      if (onSuccess) {
        onSuccess(updatedRole);
      }
      onClose();
    } catch (error) {
      logger.error("Error updating role:", error);
      if (isGlobalApiErrorModalOpen()) return;
      setError("submit", {
        type: "manual",
        message: getThunkRejectMessage(error, "Failed to update role. Please try again."),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Modal Content */}
        <div
          className="bg-surface-primary rounded-lg shadow-lg w-full max-w-md relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b border-border-primary">
            <Typography variant="h5" className="font-semibold text-text-primary">
              Edit Role
            </Typography>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={loading}
              className="!text-text-secondary hover:!text-text-primary transition-colors !border-0 !shadow-none !bg-transparent !p-0 !px-0 !py-0 !font-normal !inline-flex !items-center !justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Close modal"
            >
              <RiCloseLine className="w-5 h-5" />
            </Button>
          </div>

          {/* Modal Body */}
          <div className="p-4">
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Role Name Field */}
              <div className="mb-4">
                <Typography
                  variant="text2"
                  component="label"
                  htmlFor="roleName"
                  className="font-medium text-text-primary mb-1 block"
                >
                  Role Name
                  <Typography variant="span1" className="text-red-600 pl-1" component="span">
                    *
                  </Typography>
                </Typography>
                <Input
                  type="text"
                  {...register("roleName")}
                  className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                    errors.roleName ? "border-badge-danger" : ""
                  }`}
                  id="roleName"
                  placeholder="Enter role name"
                  label=""
                  error={errors.roleName?.message}
                  noWrapper={true}
                  disabled={loading}
                />
                <ErrorMsg message={errors.roleName?.message} className="mt-1" />
              </div>

              <div className="mb-4">
                <Typography
                  variant="text2"
                  component="label"
                  htmlFor="description"
                  className="font-medium text-text-primary mb-1 block"
                >
                  Description
                </Typography>
                <Input
                  type="text"
                  {...register("description")}
                  className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                    errors.description ? "border-badge-danger" : ""
                  }`}
                  id="description"
                  placeholder="Enter description"
                  label=""
                  error={errors.description?.message}
                  noWrapper={true}
                  disabled={loading}
                />
                <ErrorMsg message={errors.description?.message} className="mt-1" />
              </div>

              <div className="mb-4">
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      label="Status"
                      options={["Active", "In-Active"]}
                      placeholder="Select Status"
                      error={errors.status?.message}
                      disabled={loading}
                      required={true}
                      searchable={false}
                      className="text-sm"
                    />
                  )}
                />
                <ErrorMsg message={errors.status?.message} className="mt-1" />
              </div>

              {/* Submit Error */}
              {errors.submit && <ErrorMsg message={errors.submit.message} className="mb-4" />}

              {/* Submit Button */}
              <div className="flex justify-end gap-2 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  disabled={loading}
                  onClick={handleClose}
                  className="btn-reset"
                >
                  Cancel
                </Button>
                <FormSubmitButton
                  loading={loading}
                  label="Update Role"
                  loadingText="Updating..."
                  idleIconClass=""
                  size="md"
                  className="inline-block text-white font-bold transition-colors"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditRole;
